import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  fetchSessions,
  createSession as apiCreateSession,
  deleteSessionById,
  fetchSessionById,
  fetchWebliumWebsites,
  fetchWebliumWebsitePages,
  fetchWebliumWebsiteBlocks
} from '../api';
import {
  PAGE_NAME_OPTIONS,
  type CreatableSessionType,
  type PageNameOption,
  type SessionType,
  type SessionVibeBlockType,
  type WebliumWebsiteMentionItem,
  type WebliumWebsitePageMentionItem,
  type WebliumWebsiteBlockMentionItem
} from '../api.types';
import {
  LANGUAGE_OPTIONS,
  useLanguage,
  isSupportedLanguage,
  type LanguageOption
} from './LanguageDropdown';

export type SessionBasic = {
  id: string;
  name: string;
  type: SessionType;
  vibe_block: SessionVibeBlockType | null;
};

type SessionContextType = {
  selectedSession: SessionBasic | null;
  setSelectedSession: (session: SessionBasic | null) => void;
  hydrateSelectedSession?: (sessionId: string) => Promise<void>;
};

const SessionContext = createContext<SessionContextType>({
  selectedSession: null,
  setSelectedSession: () => {}
});

const getWebsiteDisplayName = (website: WebliumWebsiteMentionItem): string => (
  website.name || website.domainLabel || website.subdomain || website.id
);

const getPageDisplayName = (page: WebliumWebsitePageMentionItem): string => (
  page.label || page.name || page.id
);

const getBlockDisplayName = (block: WebliumWebsiteBlockMentionItem): string => (
  block.label || block.name || block.id
);

const getSessionTypeLabel = (type?: SessionType): string => (
  type || 'general'
);

const SESSION_DROPDOWN_MIN_WIDTH = 360;
const SESSION_DROPDOWN_MAX_WIDTH = 480;
const SESSION_DROPDOWN_VIEWPORT_MARGIN = 12;

export const useSession = () => useContext(SessionContext);

export const SessionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [selectedSession, setSelectedSession] = useState<SessionBasic | null>(null);
  const { selectedLanguage, setSelectedLanguage } = useLanguage();

  const hydrateSelectedSession = useCallback(async (sessionId: string) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;
      const data = await fetchSessionById(token, sessionId, selectedLanguage);
      // Store minimal session info; rest will be consumed by ChatInterface via actions
      setSelectedSession({
        id: data.session.id,
        name: data.session.name,
        type: data.session.type || 'general',
        vibe_block: data.session.vibe_block || null,
      });
      const sessionLanguage = data.session.language;
      if (sessionLanguage && isSupportedLanguage(sessionLanguage)) {
        setSelectedLanguage(sessionLanguage);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to hydrate session', e);
    }
  }, [selectedLanguage, setSelectedLanguage]);

  const value = useMemo(() => ({ selectedSession, setSelectedSession, hydrateSelectedSession }), [selectedSession, hydrateSelectedSession]);

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
};

type SessionDropdownProps = {
  appearance?: 'default' | 'agent';
};

const SessionDropdown: React.FC<SessionDropdownProps> = ({ appearance = 'default' }) => {
  const { selectedSession, setSelectedSession } = useSession();
  const { selectedLanguage, setSelectedLanguage } = useLanguage();

  const [open, setOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [sessions, setSessions] = useState<SessionBasic[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newSessionName, setNewSessionName] = useState('');
  const [newSessionType, setNewSessionType] = useState<CreatableSessionType>('general');
  const [newSessionLanguage, setNewSessionLanguage] = useState<LanguageOption>(selectedLanguage);
  const [newGeneralWebsiteId, setNewGeneralWebsiteId] = useState('');
  const [newGeneralPageName, setNewGeneralPageName] = useState<PageNameOption | ''>('');
  const [newVibeWebsiteId, setNewVibeWebsiteId] = useState('');
  const [newVibeBlockPageId, setNewVibeBlockPageId] = useState('');
  const [newVibeBlockId, setNewVibeBlockId] = useState('');
  const [createWebsites, setCreateWebsites] = useState<WebliumWebsiteMentionItem[]>([]);
  const [createPages, setCreatePages] = useState<WebliumWebsitePageMentionItem[]>([]);
  const [createBlocks, setCreateBlocks] = useState<WebliumWebsiteBlockMentionItem[]>([]);
  const [createWebsitesLoading, setCreateWebsitesLoading] = useState(false);
  const [createPagesLoading, setCreatePagesLoading] = useState(false);
  const [createBlocksLoading, setCreateBlocksLoading] = useState(false);
  const [createWebsitesError, setCreateWebsitesError] = useState<string | null>(null);
  const [createPagesError, setCreatePagesError] = useState<string | null>(null);
  const [createBlocksError, setCreateBlocksError] = useState<string | null>(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  useEffect(() => {
    if (open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const maxViewportWidth = Math.max(rect.width, viewportWidth - SESSION_DROPDOWN_VIEWPORT_MARGIN * 2);
      const width = Math.min(
        Math.max(rect.width, SESSION_DROPDOWN_MIN_WIDTH),
        SESSION_DROPDOWN_MAX_WIDTH,
        maxViewportWidth
      );
      const minLeft = window.scrollX + SESSION_DROPDOWN_VIEWPORT_MARGIN;
      const maxLeft = window.scrollX + viewportWidth - width - SESSION_DROPDOWN_VIEWPORT_MARGIN;
      const left = Math.max(minLeft, Math.min(rect.left + window.scrollX, maxLeft));

      setDropdownPos({ top: rect.bottom + window.scrollY, left, width });
    }
  }, [open]);

  const loadSessions = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('authToken');
      if (!token) return;
      const list = await fetchSessions(token);
      setSessions(list.map(s => ({
        id: s.id,
        name: s.name,
        type: s.type || 'general',
        vibe_block: s.vibe_block || null,
      })));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch sessions', e);
      setError('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadSessions().catch(() => {});
    }
  }, [open]);

  // Keep list in sync when selection changes (e.g., session auto-created elsewhere)
  useEffect(() => {
    loadSessions().catch((e) => {
      // eslint-disable-next-line no-console
      console.error('Failed to reload sessions', e);
      setError('Failed to reload sessions');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSession?.id]);

  useEffect(() => {
    if (!open) {
      setCreateError(null);
    }
  }, [open]);

  useEffect(() => {
    if (!showCreateModal) {
      setNewSessionName('');
      setNewSessionType('general');
      setNewGeneralWebsiteId('');
      setNewGeneralPageName('');
      setNewVibeWebsiteId('');
      setNewVibeBlockPageId('');
      setNewVibeBlockId('');
      setCreatePages([]);
      setCreateBlocks([]);
      setCreateWebsitesError(null);
      setCreatePagesError(null);
      setCreateBlocksError(null);
      setCreateError(null);
      return;
    }

    if (newSessionType === 'vibe_site') {
      setCreateWebsitesLoading(false);
      setCreateWebsitesError(null);
      return;
    }

    const token = localStorage.getItem('authToken');
    if (!token) {
      setCreateWebsitesError('Authorization token is missing.');
      setCreateWebsites([]);
      return;
    }

    let isCancelled = false;
    setCreateWebsitesLoading(true);
    setCreateWebsitesError(null);

    fetchWebliumWebsites(token)
      .then((websites) => {
        if (!isCancelled) {
          setCreateWebsites(websites);
        }
      })
      .catch((error) => {
        if (!isCancelled) {
          setCreateWebsites([]);
          setCreateWebsitesError(error instanceof Error ? error.message : 'Failed to load websites.');
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setCreateWebsitesLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [newSessionType, showCreateModal]);

  useEffect(() => {
    setCreatePages([]);
    setNewVibeBlockPageId('');
    setCreateBlocks([]);
    setNewVibeBlockId('');
    setCreatePagesError(null);
    setCreateBlocksError(null);

    if (!showCreateModal || newSessionType !== 'vibe_block' || !newVibeWebsiteId) {
      setCreatePagesLoading(false);
      return;
    }

    const token = localStorage.getItem('authToken');
    if (!token) {
      setCreatePagesError('Authorization token is missing.');
      return;
    }

    let isCancelled = false;
    setCreatePagesLoading(true);

    fetchWebliumWebsitePages(token, newVibeWebsiteId)
      .then((pages) => {
        if (!isCancelled) {
          setCreatePages(pages);
        }
      })
      .catch((error) => {
        if (!isCancelled) {
          setCreatePages([]);
          setCreatePagesError(error instanceof Error ? error.message : 'Failed to load pages.');
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setCreatePagesLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [showCreateModal, newSessionType, newVibeWebsiteId]);

  useEffect(() => {
    setCreateBlocks([]);
    setNewVibeBlockId('');
    setCreateBlocksError(null);

    if (!showCreateModal || newSessionType !== 'vibe_block' || !newVibeWebsiteId || !newVibeBlockPageId) {
      setCreateBlocksLoading(false);
      return;
    }

    const token = localStorage.getItem('authToken');
    if (!token) {
      setCreateBlocksError('Authorization token is missing.');
      return;
    }

    let isCancelled = false;
    setCreateBlocksLoading(true);

    fetchWebliumWebsiteBlocks(token, newVibeWebsiteId, newVibeBlockPageId)
      .then((blocks) => {
        if (!isCancelled) {
          setCreateBlocks(blocks);
        }
      })
      .catch((error) => {
        if (!isCancelled) {
          setCreateBlocks([]);
          setCreateBlocksError(error instanceof Error ? error.message : 'Failed to load blocks.');
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setCreateBlocksLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [showCreateModal, newSessionType, newVibeWebsiteId, newVibeBlockPageId]);

  const handleCreateSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const token = localStorage.getItem('authToken');
    if (!token) {
      setCreateError('Authorization token is missing.');
      return;
    }
    if (!newSessionName.trim()) {
      setCreateError('Session name is required.');
      return;
    }

    if (newSessionType === 'vibe_block' && (!newVibeWebsiteId || !newVibeBlockPageId || !newVibeBlockId)) {
      setCreateError('Select website, page, and block for a vibe_block session.');
      return;
    }
    setCreateLoading(true);
    setCreateError(null);

    try {
      const session = await apiCreateSession(
        token,
        newSessionName.trim(),
        [],
        newSessionLanguage,
        newSessionType === 'vibe_block'
          ? {
            type: 'vibe_block',
            vibe_block: {
              website_id: newVibeWebsiteId,
              page_id: newVibeBlockPageId,
              block_id: newVibeBlockId
            }
          }
          : newSessionType === 'vibe_site'
            ? {
              type: 'vibe_site'
            }
            : {
              type: 'general',
              websiteId: newGeneralWebsiteId || null,
              pageName: newGeneralPageName || null
            }
      );
      const basicSession: SessionBasic = {
        id: session.id,
        name: session.name,
        type: session.type || 'general',
        vibe_block: session.vibe_block || null,
      };
      setSessions(prev => [basicSession, ...prev]);
      setSelectedSession(basicSession);
      setSelectedLanguage(newSessionLanguage);

      setOpen(false);
      setShowCreateModal(false);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to create session', e);
      setCreateError(e instanceof Error ? e.message : 'Failed to create session');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDelete = async (sessionId: string) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;
      setError(null);
      await deleteSessionById(token, sessionId);
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      if (selectedSession?.id === sessionId) {
        setSelectedSession(null);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to delete session', e);
      setError(e instanceof Error ? e.message : 'Failed to delete session');
    }
  };

  const handleSelect = (session: SessionBasic) => {
    setSelectedSession(session);
    setOpen(false);
  };

  const selectedGeneralWebsite = createWebsites.find((website) => website.id === newGeneralWebsiteId) || null;
  const selectedVibeWebsite = createWebsites.find((website) => website.id === newVibeWebsiteId) || null;
  const selectedVibeBlockPage = createPages.find((page) => page.id === newVibeBlockPageId) || null;
  const selectedVibeBlock = createBlocks.find((block) => block.id === newVibeBlockId) || null;
  const canSubmitNewSession = Boolean(
    newSessionName.trim()
    && !createLoading
    && (
      newSessionType === 'general'
      || newSessionType === 'vibe_site'
      || (newVibeWebsiteId && newVibeBlockPageId && newVibeBlockId)
    )
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      setOpen((prev) => !prev);
    } else if (e.key === 'ArrowDown') {
      setOpen(true);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const openCreateSessionModal = () => {
    setOpen(false);
    setNewSessionLanguage(selectedLanguage);
    setShowCreateModal(true);
    setCreateError(null);
  };

  const dropdownList = (
    <div
      className={`${appearance === 'agent' ? 'agent-theme agent-session-menu' : ''} absolute z-[9999] flex flex-col overflow-hidden rounded-lg border border-[#EDF0F5] bg-white shadow-lg`}
      style={{
        top: dropdownPos.top,
        left: dropdownPos.left,
        width: dropdownPos.width,
        maxHeight: `calc(100vh - ${dropdownPos.top}px - ${SESSION_DROPDOWN_VIEWPORT_MARGIN}px)`
      }}
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#EDF0F5]">
        <span className="text-[#464C5C] text-sm font-semibold">Sessions</span>
        <button
          className="text-[#165bfb] text-sm font-semibold hover:underline disabled:text-[#93C5FD]"
          onClick={openCreateSessionModal}
          disabled={createLoading}
        >
          + New
        </button>
      </div>
      {loading && (
        <div className="px-4 py-3 text-sm text-[#6B7280]">Loading...</div>
      )}
      {error && (
        <div className="px-4 py-3 text-sm text-red-600">{error}</div>
      )}
      <ul className="min-h-0 overflow-y-auto" role="listbox" aria-label="Sessions list">
        {sessions.map((s) => (
          <li
            key={s.id}
            className={`px-4 py-2 flex items-center justify-between gap-2 cursor-pointer hover:bg-[#F6F8FF] ${selectedSession?.id === s.id ? 'bg-[#F6F8FF] font-bold' : ''}`}
            onClick={() => handleSelect(s)}
          >
            <span className="min-w-0 flex-1 truncate" title={s.name}>{s.name}</span>
            <span className="shrink-0 rounded-full bg-[#eef4ff] px-2 py-0.5 text-[10px] font-semibold text-[#165BFB]">
              {getSessionTypeLabel(s.type)}
            </span>
            <button
              className="text-[#9b9fa9] hover:text-red-600 text-xs"
              onClick={(e) => { e.stopPropagation(); handleDelete(s.id); }}
              aria-label={`Delete ${s.name}`}
              title="Delete session"
            >
              ✕
            </button>
          </li>
        ))}
        {sessions.length === 0 && !loading && !error && (
          <li className="px-4 py-3 text-sm text-[#6B7280]">No sessions yet</li>
        )}
      </ul>
    </div>
  );

  return (
    <div className={`${appearance === 'agent' ? 'agent-session-controls' : ''} relative flex items-center gap-3`}>
      <button
        ref={buttonRef}
        type="button"
        className="flex w-[360px] items-center justify-between gap-2 px-4 py-2 bg-white border border-[#EDF0F5] rounded-lg shadow-sm text-[#165bfb] font-semibold focus:outline-none focus:ring-2 focus:ring-[#165bfb] transition"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Select session"
        tabIndex={0}
        onClick={() => setOpen((prev) => !prev)}
        onKeyDown={handleKeyDown}
      >
        <span className="truncate">{selectedSession ? selectedSession.name : 'No session'}</span>
        <svg className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 20 20">
          <path d="M6 8l4 4 4-4" stroke="#165bfb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      <button
        type="button"
        className="whitespace-nowrap rounded-lg bg-[#165bfb] px-4 py-2 font-semibold text-white transition-colors hover:bg-[#1246d7] focus:outline-none focus:ring-2 focus:ring-[#165bfb] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
        onClick={openCreateSessionModal}
        disabled={createLoading}
      >
        + New session
      </button>
      {open && typeof window !== 'undefined' && createPortal(dropdownList, document.body)}
      {showCreateModal && typeof window !== 'undefined' && createPortal(
        <div className={`${appearance === 'agent' ? 'agent-theme agent-overlay agent-create-session' : ''} fixed inset-0 z-[10000] flex items-center justify-center bg-[#111827]/40 px-4 py-6`}>
          <form
            className="w-full max-w-[680px] overflow-hidden rounded-2xl border border-[#edf0f5] bg-white shadow-2xl"
            onSubmit={handleCreateSubmit}
          >
            <div className="flex items-start justify-between gap-4 border-b border-[#EDF0F5] px-6 py-5">
              <div>
                <h2 className="text-[22px] font-bold leading-7 text-[#111117]">Create session</h2>
                <p className="mt-1 text-[13px] text-[#6B7280]">Choose how this session should start.</p>
              </div>
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                disabled={createLoading}
                className="inline-flex size-9 items-center justify-center rounded-lg border border-[#EDF0F5] text-[#6B7280] transition hover:bg-[#F6F8FF] hover:text-[#111117] disabled:opacity-50"
                aria-label="Close create session dialog"
              >
                <svg className="size-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M18 6 6 18" />
                  <path d="m6 6 12 12" />
                </svg>
              </button>
            </div>

            <div className="max-h-[calc(100vh-170px)] overflow-y-auto px-6 py-5">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                {([
                  { type: 'general' as CreatableSessionType, label: 'general', caption: 'Open chat session' },
                  { type: 'vibe_block' as CreatableSessionType, label: 'vibe_block', caption: 'Block-focused session' },
                  { type: 'vibe_site' as CreatableSessionType, label: 'Site Brief', caption: 'Website planning session' }
                ]).map((option) => {
                  const isSelected = newSessionType === option.type;
                  return (
                    <button
                      key={option.type}
                      type="button"
                      onClick={() => {
                        setNewSessionType(option.type);
                        setCreateError(null);
                      }}
                      className={`group relative flex min-h-[148px] flex-col items-center justify-between rounded-2xl border-2 p-4 text-center transition ${
                        isSelected
                          ? 'border-[#165BFB] bg-[#eef4ff] shadow-[0_12px_30px_rgba(22,91,251,0.16)]'
                          : 'border-[#EDF0F5] bg-white hover:border-[#b8d1ff] hover:bg-[#f8fbff]'
                      }`}
                      aria-pressed={isSelected}
                    >
                      <span className={`absolute right-3 top-3 inline-flex size-6 items-center justify-center rounded-full border ${
                        isSelected ? 'border-[#165BFB] bg-[#165BFB]' : 'border-[#dbe7ff] bg-white'
                      }`}>
                        {isSelected && (
                          <span className="size-2.5 rounded-full bg-white" />
                        )}
                      </span>
                      <span className={`mt-4 inline-flex size-14 items-center justify-center rounded-2xl ${
                        isSelected ? 'bg-[#165BFB] text-white' : 'bg-[#f1f5ff] text-[#165BFB]'
                      }`}>
                        {option.type === 'general' ? (
                          <svg className="size-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 12a8.5 8.5 0 0 1-8.5 8.5H5l-2 2v-7.5A8.5 8.5 0 1 1 21 12Z" />
                            <path d="M8 11h8" />
                            <path d="M8 15h5" />
                          </svg>
                        ) : option.type === 'vibe_block' ? (
                          <svg className="size-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M4 5.5A1.5 1.5 0 0 1 5.5 4h5A1.5 1.5 0 0 1 12 5.5v5a1.5 1.5 0 0 1-1.5 1.5h-5A1.5 1.5 0 0 1 4 10.5v-5Z" />
                            <path d="M14 13.5a1.5 1.5 0 0 1 1.5-1.5h3a1.5 1.5 0 0 1 1.5 1.5v5a1.5 1.5 0 0 1-1.5 1.5h-3a1.5 1.5 0 0 1-1.5-1.5v-5Z" />
                            <path d="M15 4h4" />
                            <path d="M17 4v6" />
                            <path d="M4 16h6" />
                            <path d="M7 14v4" />
                          </svg>
                        ) : (
                          <svg className="size-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M7 3.5h7l4 4V20a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 6 20V5a1.5 1.5 0 0 1 1-1.5Z" />
                            <path d="M14 3.5V8h4" />
                            <path d="M9 12h6" />
                            <path d="M9 16h4" />
                          </svg>
                        )}
                      </span>
                      <span className="mt-4">
                        <span className="block text-[18px] font-bold text-[#111117]">{option.label}</span>
                        <span className="mt-1 block text-[12px] text-[#6B7280]">{option.caption}</span>
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="mt-5 rounded-2xl border border-[#EDF0F5] bg-[#F8FBFF] p-4">
                {newSessionType === 'general' ? (
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="flex flex-col gap-2">
                      <label className="text-[12px] font-semibold text-[#6B7280]" htmlFor="new-general-website">
                        Website <span className="font-normal text-[#9CA3AF]">(optional)</span>
                      </label>
                      <select
                        id="new-general-website"
                        value={newGeneralWebsiteId}
                        onChange={(event) => {
                          setNewGeneralWebsiteId(event.target.value);
                          setCreateError(null);
                        }}
                        disabled={createLoading || createWebsitesLoading}
                        className="h-11 rounded-xl border border-[#EDF0F5] bg-white px-3 text-[14px] outline-none transition focus:border-[#165bfb] focus:ring-2 focus:ring-[#dbe7ff] disabled:bg-[#f6f8ff] disabled:text-[#9CA3AF]"
                      >
                        <option value="">{createWebsitesLoading ? 'Loading websites...' : 'No website'}</option>
                        {createWebsites.map((website) => (
                          <option key={website.id} value={website.id}>{getWebsiteDisplayName(website)}</option>
                        ))}
                      </select>
                      {selectedGeneralWebsite && (
                        <span className="truncate text-[12px] text-[#6B7280]">{selectedGeneralWebsite.domainLabel}</span>
                      )}
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-[12px] font-semibold text-[#6B7280]" htmlFor="new-general-page">
                        Page name <span className="font-normal text-[#9CA3AF]">(optional)</span>
                      </label>
                      <select
                        id="new-general-page"
                        value={newGeneralPageName}
                        onChange={(event) => {
                          setNewGeneralPageName(event.target.value as PageNameOption | '');
                          setCreateError(null);
                        }}
                        disabled={createLoading}
                        className="h-11 rounded-xl border border-[#EDF0F5] bg-white px-3 text-[14px] outline-none transition focus:border-[#165bfb] focus:ring-2 focus:ring-[#dbe7ff] disabled:bg-[#f6f8ff]"
                      >
                        <option value="">No page name</option>
                        {PAGE_NAME_OPTIONS.map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                ) : newSessionType === 'vibe_site' ? (
                  <div className="rounded-xl border border-[#dbe7ff] bg-white px-4 py-3 text-[13px] leading-5 text-[#5f6f89]">
                    The website will be generated after the brief requirements and site structure are ready.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-4">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                      <div className="flex flex-col gap-2">
                        <label className="text-[12px] font-semibold text-[#6B7280]" htmlFor="new-vibe-website">Website</label>
                        <select
                          id="new-vibe-website"
                          value={newVibeWebsiteId}
                          onChange={(event) => {
                            setNewVibeWebsiteId(event.target.value);
                            setCreateError(null);
                          }}
                          disabled={createLoading || createWebsitesLoading}
                          className="h-11 rounded-xl border border-[#EDF0F5] bg-white px-3 text-[14px] outline-none transition focus:border-[#165bfb] focus:ring-2 focus:ring-[#dbe7ff] disabled:bg-[#f6f8ff] disabled:text-[#9CA3AF]"
                        >
                          <option value="">{createWebsitesLoading ? 'Loading...' : 'Select website'}</option>
                          {createWebsites.map((website) => (
                            <option key={website.id} value={website.id}>{getWebsiteDisplayName(website)}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className="text-[12px] font-semibold text-[#6B7280]" htmlFor="new-vibe-page">Page</label>
                        <select
                          id="new-vibe-page"
                          value={newVibeBlockPageId}
                          onChange={(event) => {
                            setNewVibeBlockPageId(event.target.value);
                            setCreateError(null);
                          }}
                          disabled={createLoading || !newVibeWebsiteId || createPagesLoading}
                          className="h-11 rounded-xl border border-[#EDF0F5] bg-white px-3 text-[14px] outline-none transition focus:border-[#165bfb] focus:ring-2 focus:ring-[#dbe7ff] disabled:bg-[#f6f8ff] disabled:text-[#9CA3AF]"
                        >
                          <option value="">{createPagesLoading ? 'Loading...' : 'Select page'}</option>
                          {createPages.map((page) => (
                            <option key={page.id} value={page.id}>{getPageDisplayName(page)}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className="text-[12px] font-semibold text-[#6B7280]" htmlFor="new-vibe-block">Block</label>
                        <select
                          id="new-vibe-block"
                          value={newVibeBlockId}
                          onChange={(event) => {
                            setNewVibeBlockId(event.target.value);
                            setCreateError(null);
                          }}
                          disabled={createLoading || !newVibeWebsiteId || !newVibeBlockPageId || createBlocksLoading}
                          className="h-11 rounded-xl border border-[#EDF0F5] bg-white px-3 text-[14px] outline-none transition focus:border-[#165bfb] focus:ring-2 focus:ring-[#dbe7ff] disabled:bg-[#f6f8ff] disabled:text-[#9CA3AF]"
                        >
                          <option value="">{createBlocksLoading ? 'Loading...' : 'Select block'}</option>
                          {createBlocks.map((block) => (
                            <option key={block.id} value={block.id}>{getBlockDisplayName(block)}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {(selectedVibeWebsite || selectedVibeBlockPage || selectedVibeBlock) && (
                      <div className="rounded-xl border border-[#dbe7ff] bg-white px-3 py-2 text-[12px] text-[#5f6f89]">
                        <span className="font-semibold text-[#27344a]">Scope:</span>{' '}
                        {selectedVibeWebsite ? getWebsiteDisplayName(selectedVibeWebsite) : 'Website'} /{' '}
                        {selectedVibeBlockPage ? getPageDisplayName(selectedVibeBlockPage) : 'Page'} /{' '}
                        {selectedVibeBlock ? getBlockDisplayName(selectedVibeBlock) : 'Block'}
                      </div>
                    )}
                  </div>
                )}

                {newSessionType !== 'vibe_site' && (createWebsitesError || createPagesError || createBlocksError) && (
                  <div className="mt-3 text-[12px] text-[#dc2626]">
                    {createWebsitesError || createPagesError || createBlocksError}
                  </div>
                )}
              </div>

              <div className="mt-5 flex flex-col gap-2">
                <label className="text-[12px] font-semibold uppercase tracking-[0.04em] text-[#6B7280]" htmlFor="new-session-language">
                  Language
                </label>
                <select
                  id="new-session-language"
                  value={newSessionLanguage}
                  onChange={(event) => {
                    setNewSessionLanguage(event.target.value as LanguageOption);
                    setCreateError(null);
                  }}
                  disabled={createLoading}
                  className="h-12 rounded-xl border border-[#EDF0F5] bg-white px-4 text-[15px] outline-none transition focus:border-[#165bfb] focus:ring-2 focus:ring-[#dbe7ff] disabled:bg-[#f6f8ff] disabled:text-[#9CA3AF]"
                >
                  {LANGUAGE_OPTIONS.map((language) => (
                    <option key={language} value={language}>{language}</option>
                  ))}
                </select>
              </div>

              <div className="mt-5 rounded-2xl border border-[#EDF0F5] bg-[#F8FBFF] p-4">
                <div className="flex flex-col gap-2">
                  <label className="text-[12px] font-semibold uppercase tracking-[0.04em] text-[#6B7280]" htmlFor="new-session-name">Session name</label>
                  <input
                    id="new-session-name"
                    type="text"
                    value={newSessionName}
                    onChange={(event) => {
                      setNewSessionName(event.target.value);
                      setCreateError(null);
                    }}
                    className="h-12 rounded-xl border border-[#EDF0F5] bg-white px-4 text-[15px] outline-none transition focus:border-[#165bfb] focus:ring-2 focus:ring-[#dbe7ff]"
                    placeholder="Enter session name"
                    disabled={createLoading}
                    autoFocus
                  />
                </div>
              </div>

              {createError && (
                <div className="mt-4 rounded-xl border border-[#fecaca] bg-[#fef2f2] px-3 py-2 text-[13px] text-[#dc2626]">
                  {createError}
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-[#EDF0F5] bg-white px-6 py-4">
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                disabled={createLoading}
                className="h-11 rounded-xl border border-[#EDF0F5] px-5 font-semibold text-[#464C5C] transition hover:bg-[#F6F8FF] disabled:cursor-not-allowed disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="submit"
                className={`h-11 rounded-xl px-5 font-semibold transition-colors ${
                  canSubmitNewSession
                    ? 'bg-[#165bfb] text-white hover:bg-[#1246d7]'
                    : 'cursor-not-allowed bg-[#cdd7ff] text-white'
                }`}
                disabled={!canSubmitNewSession}
              >
                {createLoading ? 'Creating...' : 'Create session'}
              </button>
            </div>
          </form>
        </div>,
        document.body
      )}
    </div>
  );
};

export default SessionDropdown;
